import { ISubcard } from '../../cards/multicards.interface';
import { ReviewState, ReviewStore } from '../review.interface';

export const MULTICARD_SEPARATOR = '\n\n---\n\n';

export const getSubcardMarkup = (subcard: ISubcard, reviewState: ReviewState): string => {
    if (reviewState === ReviewState.SubcardQuestion) {
        return subcard.question;
    }
    return `${subcard.question}${MULTICARD_SEPARATOR}${subcard.answer}`;
};

export const isLastSubcard = (store: ReviewStore): boolean => {
    if (!store.isMultiCard) {
        return false;
    }
    return !hasMoreSubcards(store.allSubcards.length, store.viewedSubcardIndices);
};

export const getCurrentSubcard = (store: ReviewStore): ISubcard | undefined => {
    if (!store.isMultiCard || store.allSubcards.length === 0) {
        return undefined;
    }
    return store.allSubcards[store.currentSubcardIndex];
};

export const getSubcardProgress = (store: ReviewStore): { current: number; total: number } | undefined => {
    if (!store.isMultiCard) {
        return undefined;
    }
    return {
        current: store.viewedSubcardIndices.length,
        total: store.allSubcards.length
    };
};

const groupOf = (subcard: ISubcard): string => subcard.group ?? '';

const hasGroup = (subcard: ISubcard): boolean => subcard.group !== undefined && subcard.group.length > 0;

export const getGroupProgress = (store: ReviewStore): { current: number; total: number } | undefined => {
    if (!store.isMultiCard || !store.allSubcards.some(hasGroup)) {
        return undefined;
    }

    const totalGroups = new Set(store.allSubcards.map(groupOf));
    const viewedGroups = new Set(store.viewedSubcardIndices.map(index => groupOf(store.allSubcards[index])));

    return {
        current: viewedGroups.size,
        total: totalGroups.size
    };
};

export const shouldShowSubcardButtons = (store: ReviewStore): boolean => {
    return store.isMultiCard && store.state === ReviewState.SubcardAnswer && 
           hasMoreSubcards(store.allSubcards.length, store.viewedSubcardIndices);
};

export const shouldShowSubmitButtons = (store: ReviewStore): boolean => {
    if (!store.isMultiCard) {
        return store.state === ReviewState.Answer;
    }
    
    return store.state === ReviewState.Answer || 
           (store.state === ReviewState.SubcardAnswer && !hasMoreSubcards(store.allSubcards.length, store.viewedSubcardIndices));
};

export const calculateTotalTime = (timings: number[]): number => {
    return timings.reduce((sum, time) => sum + time, 0);
};

const pickRandom = <T>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

export const getNextSubcardIndex = (allSubcards: ISubcard[], viewedIndices: number[]): number | undefined => {
    const availableIndices = Array.from(Array(allSubcards.length).keys())
        .filter(index => !viewedIndices.includes(index));

    if (availableIndices.length === 0) {
        return undefined;
    }

    const hasGroups = allSubcards.some(hasGroup);
    if (!hasGroups) {
        return pickRandom(availableIndices);
    }

    const viewedCountByGroup = new Map<string, number>();
    for (const index of viewedIndices) {
        const group = groupOf(allSubcards[index]);
        viewedCountByGroup.set(group, (viewedCountByGroup.get(group) ?? 0) + 1);
    }

    const lastGroup = viewedIndices.length > 0
        ? groupOf(allSubcards[viewedIndices[viewedIndices.length - 1]])
        : undefined;

    let candidateGroups = Array.from(new Set(availableIndices.map(index => groupOf(allSubcards[index]))));
    if (candidateGroups.length > 1 && lastGroup !== undefined) {
        candidateGroups = candidateGroups.filter(group => group !== lastGroup);
    }

    const minViewed = Math.min(...candidateGroups.map(group => viewedCountByGroup.get(group) ?? 0));
    const nextGroup = pickRandom(candidateGroups.filter(group => (viewedCountByGroup.get(group) ?? 0) === minViewed));

    return pickRandom(availableIndices.filter(index => groupOf(allSubcards[index]) === nextGroup));
};

/**
 * Checks if there are more subcards available to view
 * @param totalSubcards - Total number of subcards
 * @param viewedIndices - Array of indices that have already been viewed
 * @returns True if there are more subcards to view
 */
export const hasMoreSubcards = (totalSubcards: number, viewedIndices: number[]): boolean => {
    return viewedIndices.length < totalSubcards;
};